import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { MassUnit, VolumeUnit } from '@/lib/uom';

const VALID_MASS: MassUnit[] = ['g', 'kg', 'lb', 'oz'];
const VALID_VOLUME: VolumeUnit[] = ['ml', 'l', 'floz', 'gal'];

/**
 * Reads the current user's preferred mass + volume units. NULL = no tooltip
 * conversion for that dimension. Used by ColumnTooltip to render an
 * "≈ X lb" / "≈ X L" hint over native values.
 */
export function useDisplayUnits(): {
  tooltipMassUnit: MassUnit | null;
  tooltipVolumeUnit: VolumeUnit | null;
} {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ['user-display-units', user?.id],
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 10,
    gcTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('users')
        .select('preferred_mass_unit, preferred_volume_unit')
        .eq('id', user!.id)
        .maybeSingle();
      if (error) {
        console.warn('[useDisplayUnits] failed:', error.message);
        return { preferred_mass_unit: null as string | null, preferred_volume_unit: null as string | null };
      }
      return data ?? { preferred_mass_unit: null, preferred_volume_unit: null };
    },
  });
  const rawMass = data?.preferred_mass_unit ?? null;
  const massUnit = rawMass && (VALID_MASS as string[]).includes(rawMass) ? (rawMass as MassUnit) : null;
  const rawVol = (data as { preferred_volume_unit?: string | null } | undefined)?.preferred_volume_unit ?? null;
  const volumeUnit = rawVol && (VALID_VOLUME as string[]).includes(rawVol) ? (rawVol as VolumeUnit) : null;
  return { tooltipMassUnit: massUnit, tooltipVolumeUnit: volumeUnit };
}